import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const PageHeader = styled.div`
  padding: 8rem 0 4rem;
  background-color: var(--color-primary-dark);
  color: var(--color-light);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to right bottom, var(--color-primary-dark) 49%, var(--color-white) 50%);
  }
`;

const PageHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  max-width: 600px;
`;

const MissionSection = styled.section`
  padding: 6rem 0;
`;

const MissionContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const MissionText = styled.div``;

const MissionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MissionStatement = styled.blockquote`
  font-size: 1.5rem;
  line-height: 1.6;
  color: var(--color-primary);
  font-style: italic;
  padding-left: 2rem;
  border-left: 4px solid var(--color-accent);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const MissionDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const MissionImageContainer = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  height: 400px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const MissionImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url('/images/mission.jpg');
  background-size: cover;
  background-position: center;
`;

const ValuesSection = styled.section`
  padding: 6rem 0;
  background-color: var(--color-gray);
`;

const ValuesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ValuesTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const ValueCard = styled.div`
  background: var(--color-white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const ValueIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-accent);
  margin-bottom: 1.5rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ValueDescription = styled.p`
  line-height: 1.7;
`;

const TeamSection = styled.section`
  padding: 6rem 0;
`;

const TeamContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const TeamTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TeamSubtitle = styled.p`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const TeamMember = styled.div`
  background: var(--color-white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const MemberImageContainer = styled.div`
  height: 250px;
  overflow: hidden;
`;

const MemberImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
  
  ${TeamMember}:hover & {
    transform: scale(1.05);
  }
`;

const MemberInfo = styled.div`
  padding: 1.5rem;
`;

const MemberName = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const MemberPosition = styled.p`
  color: var(--color-accent);
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const MemberBio = styled.p`
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--color-primary);
`;

const CtaSection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-primary);
  color: var(--color-light);
  text-align: center;
`;

const CtaContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CtaText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2.5rem;
  background: var(--color-accent);
  color: var(--color-white);
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-white);
    color: var(--color-primary);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const About = () => {
  return (
    <>
      <PageHeader>
        <PageHeaderContent>
          <PageTitle>About Kriegerlabs</PageTitle>
          <PageSubtitle>
            Get to know our philosophy, values, and the team behind our innovative solutions
          </PageSubtitle>
        </PageHeaderContent>
      </PageHeader>
      
      <MissionSection>
        <MissionContent>
          <MissionText>
            <MissionTitle>Our Mission</MissionTitle>
            <MissionStatement>
              "Kriegerlabs exists to architect intelligent, scalable, and resilient technology solutions that bridge vision and execution. We transform complexity into clarity, guiding organizations through innovation with precision, integrity, and a relentless focus on quality."
            </MissionStatement>
            <MissionDescription>
              Founded on the principle that technology should serve business goals, not the other way around, we bring strategic thinking to every project. Our approach combines technical excellence with practical business acumen, ensuring solutions that are not just technically sound, but strategically valuable.
            </MissionDescription>
            <MissionDescription>
              With decades of collective experience across diverse industries, we understand the common pitfalls of technology initiatives and how to avoid them. Our guidance helps organizations make confident decisions aligned with their long-term vision.
            </MissionDescription>
          </MissionText>
          
          <MissionImageContainer>
            <MissionImage />
          </MissionImageContainer>
        </MissionContent>
      </MissionSection>
      
      <ValuesSection>
        <ValuesContainer>
          <ValuesTitle>Our Core Values</ValuesTitle>
          <ValuesGrid>
            <ValueCard>
              <ValueIcon>
                <i className="fas fa-lightbulb"></i>
              </ValueIcon>
              <ValueTitle>Intelligent Solutions</ValueTitle>
              <ValueDescription>
                We seek the smartest path forward, not just the obvious one. Our solutions reflect thoughtful consideration of both immediate needs and future possibilities.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <i className="fas fa-expand-arrows-alt"></i>
              </ValueIcon>
              <ValueTitle>Scalability</ValueTitle>
              <ValueDescription>
                We design with growth in mind, creating architectures that can evolve alongside your business without requiring constant reinvention.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <i className="fas fa-shield-alt"></i>
              </ValueIcon>
              <ValueTitle>Resilience</ValueTitle>
              <ValueDescription>
                Our solutions are built to withstand not just today's challenges, but tomorrow's unforeseen obstacles, ensuring continuity and stability.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <i className="fas fa-handshake"></i>
              </ValueIcon>
              <ValueTitle>Integrity</ValueTitle>
              <ValueDescription>
                We prioritize honesty and transparency in all our interactions, building relationships based on mutual trust and respect.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <i className="fas fa-gem"></i>
              </ValueIcon>
              <ValueTitle>Quality</ValueTitle>
              <ValueDescription>
                We never compromise on excellence. Our relentless pursuit of quality ensures solutions that stand the test of time.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard>
              <ValueIcon>
                <i className="fas fa-glasses"></i>
              </ValueIcon>
              <ValueTitle>Clarity</ValueTitle>
              <ValueDescription>
                We transform complexity into understandable concepts, ensuring all stakeholders can grasp and contribute to the technical vision.
              </ValueDescription>
            </ValueCard>
          </ValuesGrid>
        </ValuesContainer>
      </ValuesSection>
      
      <TeamSection>
        <TeamContainer>
          <TeamTitle>Meet Our Team</TeamTitle>
          <TeamSubtitle>
            Our team combines deep technical expertise with strategic business acumen to deliver transformative solutions.
          </TeamSubtitle>
          
          <TeamGrid>
            <TeamMember>
              <MemberImageContainer>
                <MemberImage style={{ backgroundImage: 'url("/images/team1.jpg")' }} />
              </MemberImageContainer>
              <MemberInfo>
                <MemberName>Chris Krieger</MemberName>
                <MemberPosition>Founder & Chief Architect</MemberPosition>
                <MemberBio>
                  With over 15 years of experience in system architecture and technology leadership, Chris brings vision and strategic direction to every project.
                </MemberBio>
              </MemberInfo>
            </TeamMember>
            
            <TeamMember>
              <MemberImageContainer>
                <MemberImage style={{ backgroundImage: 'url("/images/team2.jpg")' }} />
              </MemberImageContainer>
              <MemberInfo>
                <MemberName>Tim</MemberName>
                <MemberPosition>Technical Lead</MemberPosition>
                <MemberBio>
                  Tim specializes in scalable infrastructure and cloud architectures, with extensive experience in AWS, Azure, and Google Cloud platforms.
                </MemberBio>
              </MemberInfo>
            </TeamMember>
            
            <TeamMember>
              <MemberImageContainer>
                <MemberImage style={{ backgroundImage: 'url("/images/team3.jpg")' }} />
              </MemberImageContainer>
              <MemberInfo>
                <MemberName>Josh</MemberName>
                <MemberPosition>Solution Architect</MemberPosition>
                <MemberBio>
                  Josh excels at translating business requirements into technical specifications, ensuring solutions meet both immediate and long-term needs.
                </MemberBio>
              </MemberInfo>
            </TeamMember>
            
            <TeamMember>
              <MemberImageContainer>
                <MemberImage style={{ backgroundImage: 'url("/images/team4.jpg")' }} />
              </MemberImageContainer>
              <MemberInfo>
                <MemberName>Paul</MemberName>
                <MemberPosition>Security Specialist</MemberPosition>
                <MemberBio>
                  Paul brings deep expertise in cybersecurity architecture, ensuring all solutions are built with security as a fundamental design principle.
                </MemberBio>
              </MemberInfo>
            </TeamMember>
          </TeamGrid>
        </TeamContainer>
      </TeamSection>
      
      <CtaSection>
        <CtaContainer>
          <CtaTitle>Ready to Work Together?</CtaTitle>
          <CtaText>
            Let's discuss how Kriegerlabs can help architect solutions for your organization's unique challenges.
          </CtaText>
          <CtaButton to="/contact">Get in Touch</CtaButton>
        </CtaContainer>
      </CtaSection>
    </>
  );
};

export default About;
